import axios, {
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from "axios";
import { Level, reportError } from "./owl";
import { CODE_ENV, CURRENT_CODE_ENV, TIMEOUT } from "./constant";
import { isServer } from "./constant";
import { MSIRequestSuccessCbParams, request } from "./bridge/request";
import { getAppEnv } from "./getBrowserEnv";
import { loadH5Guard } from "./loadH5Guard";

// 环境配置类型
type EnvConfig = {
  baseURL: string;
  timeout: number;
  withCredentials?: boolean;
};

// 响应数据结构
export interface ResponseData<T = any> {
  traceId: string;
  code: number;
  message: string;
  data: T;
}

declare module "axios" {
  interface AxiosResponse<T = any> {
    traceId: string;
    code: number;
    message: string;
    data: T; // 保持原有data结构
  }
}

/**
 * 为 GET 请求序列化参数，支持传递数组
 * @param params 参数对象
 * @returns 序列化后的参数字符串
 */
function serializeParams(params: any): string {
  if (!params) return '';
  const parts: string[] = [];
  
  const encode = (val: any) => {
    return encodeURIComponent(val).replace(/%20/g, '+');
  };
  
  const processParam = (key: string, value: any) => {
    if (value === null || typeof value === 'undefined') {
      return;
    }

    if (Array.isArray(value)) {
      // 将数组参数格式化为逗号分隔的形式，如 params=1,2,3,4
      parts.push(`${encode(key)}=${value.map(encode).join(',')}`);
    } else if (typeof value === 'object') {
      Object.keys(value).forEach((nestedKey) => {
        processParam(`${key}[${nestedKey}]`, value[nestedKey]);
      });
    } else {
      parts.push(`${encode(key)}=${encode(value)}`);
    }
  };
  
  Object.keys(params).forEach((key) => {
    processParam(key, params[key]);
  });
  
  return parts.join('&');
}

class HttpClient {
  private isNeedCallBridge: boolean = false; // 是否需要调用桥方法发请求
  private instance: AxiosInstance | null = null; // axios 实例
  private config: EnvConfig; // 环境配置

  constructor(config: EnvConfig) {
    // h5guard 不支持外投容器，因此在外投容器中需要使用桥方法发起请求
    // 外投容器中使用 MSI 桥，外投容器支持携带 cookie，但是有 APP 最低版本要求
    // Android: 12.26.400，iOS: 12.37.200
    this.isNeedCallBridge = getAppEnv().isGrowthContainer;
    this.config = config;

    if (!this.isNeedCallBridge) {
      // 使用 Axios 实例，进行初始化
      this.instance = axios.create({
        baseURL: config.baseURL,
        timeout: config.timeout,
        withCredentials: true,
        headers: { "Content-Type": "application/json;charset=UTF-8" },
      });
      this.setupInterceptors();
    }
  }

  // 初始化拦截器
  private setupInterceptors(): void {
    if (!this.instance) return;

    // 请求拦截
    this.instance.interceptors.request.use(
      (config: InternalAxiosRequestConfig) => {
        // 合并自定义 headers 和默认 headers
        const defaultHeaders: Record<string, string> = {
          "Content-Type": "application/json;charset=UTF-8",
        };
        if (CURRENT_CODE_ENV === CODE_ENV.STAGING) {
          defaultHeaders["gray-set"] = "true";
        }
        // 测试环境添加泳道标识
        if ([CODE_ENV.TEST, CODE_ENV.DEVELOP].includes(CURRENT_CODE_ENV)) {
          // defaultHeaders["swimlane"] = "liuyu99-ynviw";
        }
        const mergedHeaders = Object.assign({}, defaultHeaders, config?.headers || {});
        config.headers = mergedHeaders;

        return config;
      },
      // eslint-disable-next-line
      (error) => Promise.reject(error),
    );

    // 响应拦截
    this.instance.interceptors.response.use(
      (response: AxiosResponse<ResponseData>) => {
        // 业务状态码处理
        if (response.data.code !== 0 && response.data.code !== 200) {
          this.handleError(response);
          return Promise.reject(response.data);
        }
        return {
          ...response?.data,
          traceId: response?.headers?.["M-TraceId"] || response?.headers?.["m-traceid"] || "",
        } as AxiosResponse<ResponseData>;
      },
      (error) => {
        this.handleError(error);
        return Promise.reject(error);
      },
    );
  }

  // 基础请求方法
  public request<T = any>(config: AxiosRequestConfig): Promise<T> {
    if (this.isNeedCallBridge) {
      return Promise.reject("not support");
    }
    if (!this.instance) {
      return Promise.reject("HttpClient instance is not initialized");
    }
    return this.instance.request(config);
  }

  public async get<Params = any, T = any>({
    url,
    data,
    config,
  }: {
    url: string;
    data?: Params;
    config?: AxiosRequestConfig;
  }): Promise<ResponseData<T>> {
    if (this.isNeedCallBridge) {
      // 调用桥方法请求
      return new Promise<ResponseData<T>>((resolve, reject) => {
        // 构造请求头
        const headers = this.handleBridgeRequestHeaders(config);
        let requestUrl = this.config?.baseURL + url;
        
        // 参数序列化 TODO 验证
        if (data) {
          const serializedParams = serializeParams(data);
          if (serializedParams) {
            requestUrl += (requestUrl.includes('?') ? '&' : '?') + serializedParams;
          }
        }
        
        request({
          url: requestUrl,
          method: "GET",
          header: headers,
          timeout: this.config?.timeout,
          onSuccess: (result) => {
            const response = this.handleBridgeResponse(result, requestUrl);
            if (response.data.code !== 0 && response.data.code !== 200) {
              this.handleError(response);
              reject(response.data);
            } else {
              resolve(response.data as ResponseData<T>);
            }
          },
          onFail: (err) => {
            console.error("[hpr] get-request-error", err);
            this.handleError(err);
            reject(err);
          },
        });
      });
    } else {
      // 使用 Axios 请求
      if (this.instance) {
        // H5Guard 兜底：模板未加载成功时，await 加载确保加签就绪
        if (!isServer && typeof H5guard === "undefined") {
          await loadH5Guard();
        }
        return this.instance.get(url, {
          ...config,
          params: data,
          paramsSerializer: (params) => {
            return serializeParams(params);
          },
        });
      } else {
        reportError({
          name: "[request] requestError",
          msg: "HttpClient instance is not initialized",
          category: "baseError",
          level: Level.ERROR,
        });
        return Promise.reject("HttpClient instance is not initialized");
      }
    }
  }

  public async post<Params = any, T = any>({
    url,
    data,
    config,
  }: {
    url: string;
    data?: Params;
    config?: AxiosRequestConfig;
  }): Promise<ResponseData<T>> {
    if (this.isNeedCallBridge) {
      // 调用桥方法请求
      return new Promise<ResponseData<T>>((resolve, reject) => {
        // 构造请求头
        const headers = this.handleBridgeRequestHeaders(config);
        const requestUrl = this.config?.baseURL + url;
        request({
          url: requestUrl,
          method: "POST",
          data,
          header: headers,
          timeout: this.config?.timeout,
          onSuccess: (result) => {
            const response = this.handleBridgeResponse(result, requestUrl);
            if (response.data.code !== 0 && response.data.code !== 200) {
              this.handleError(response);
              reject(response.data);
            } else {
              resolve(response.data as ResponseData<T>);
            }
          },
          onFail: (err) => {
            console.error("[hpr] post-request-error", err);
            this.handleError(err);
            reject(err);
          },
        });
      });
    } else {
      // 使用 Axios 请求
      if (this.instance) {
        // H5Guard 兜底：模板未加载成功时，await 加载确保加签就绪
        if (!isServer && typeof H5guard === "undefined") {
          await loadH5Guard();
        }
        return this.instance.post(url, data, config);
      } else {
        reportError({
          name: "[request] requestError",
          msg: "HttpClient instance is not initialized",
          category: "baseError",
          level: Level.ERROR,
        });
        return Promise.reject("HttpClient instance is not initialized");
      }
    }
  }

  private handleError(error: any): void {
    try {
      // axios 使用 status, MSI 桥方法使用 statusCode
      const responseStatus = error?.status || 500;
      const responseDataCode = error?.data?.code || 0;
      const requestUrl = error?.config?.url || "";
      const reportTags = {
        traceId: error?.headers?.["M-TraceId"] || error?.headers?.["m-traceid"] || "",
      };
      if (responseStatus === 200) {
        // 网络请求正常，业务异常
        reportError({
          name: `${responseDataCode}-${requestUrl}`,
          msg: error,
          category: "ajaxError",
          level: Level.ERROR,
          tags: reportTags,
        });
      } else {
        // 网络异常
        reportError({
          name: `${responseStatus}-${requestUrl}`,
          msg: error,
          category: "ajaxError",
          level: Level.ERROR,
          tags: reportTags,
        });
      }
    } catch (err) {
      reportError({
        name: "[request] handleError error",
        msg: error,
        category: "jsError",
        level: Level.ERROR,
      });
    }
  }
  // 处理桥方法的请求头
  private handleBridgeRequestHeaders(config?: AxiosRequestConfig): Record<string, string> {
    const headers = {
      "Content-Type": "application/json;charset=UTF-8",
      ...(config?.headers || {}),
    } as Record<string, string>;
    if (CURRENT_CODE_ENV === CODE_ENV.STAGING) {
      headers["gray-set"] = "true";
    }
    return headers;
  }

  // 处理桥方法的响应
  private handleBridgeResponse(response: MSIRequestSuccessCbParams, requestUrl: string) {
    return {
      ...response,
      status: response?.statusCode,
      config: {
        url: requestUrl,
      },
    };
  }
}

export enum DomainType {
  MEDIA = "media",
  MEDIA_V2 = "mediaV2",
  PEPPER = "pepper",
  MAP = "map",
  CLICK = "click",
}

// 环境配置
const DomainConfigs: Record<DomainType, Record<CODE_ENV, string>> = {
  [DomainType.MEDIA]: {
    [CODE_ENV.DEVELOP]: "http://mall.pepper.test.sankuai.com",
    [CODE_ENV.TEST]: "http://mall.pepper.test.sankuai.com",
    [CODE_ENV.STAGING]: "https://media.st.meituan.com",
    [CODE_ENV.PRODUCTION]: "https://media.meituan.com",
  },
  [DomainType.MEDIA_V2]: {
    [CODE_ENV.DEVELOP]: "http://mall.pepper.test.sankuai.com",
    [CODE_ENV.TEST]: "http://mall.pepper.test.sankuai.com",
    [CODE_ENV.STAGING]: "https://media2.st.meituan.com",
    [CODE_ENV.PRODUCTION]: "https://media.meituan.com",
  },
  [DomainType.PEPPER]: {
    [CODE_ENV.DEVELOP]: "http://pepper.mall.test.sankuai.com",
    [CODE_ENV.TEST]: "http://pepper.mall.test.sankuai.com",
    [CODE_ENV.STAGING]: "https://peppermall.meituan.com",
    [CODE_ENV.PRODUCTION]: "https://peppermall.meituan.com",
  },
  [DomainType.MAP]: {
    [CODE_ENV.DEVELOP]: "http://lbsapi.map.test.sankuai.com",
    [CODE_ENV.TEST]: "http://lbsapi.map.test.sankuai.com",
    [CODE_ENV.STAGING]: "https://lbsapi.map.st.sankuai.com",
    [CODE_ENV.PRODUCTION]: "https://lbsapi.sankuai.com",
  },
  [DomainType.CLICK]: {
    [CODE_ENV.DEVELOP]: "http://click.tsp.test.sankuai.com", // 开发环境使用相对路径，通过 Vite 代理转发
    [CODE_ENV.TEST]: "https://click.tsp.test.sankuai.com",
    [CODE_ENV.STAGING]: "https://click.st.meituan.com",
    [CODE_ENV.PRODUCTION]: "https://click.meituan.com",
  },
};

// SSR (服务端渲染) 环境配置
//
// 服务端渲染时需要使用完整的外部域名,不能使用相对路径
export const SSRDomainConfigs: Record<DomainType, Record<CODE_ENV, string>> = {
  // cps.union.giant.st.sankuai.com
  [DomainType.MEDIA]: {
    [CODE_ENV.DEVELOP]: "http://mall.pepper.test.sankuai.com",
    [CODE_ENV.TEST]: "http://mall.pepper.test.sankuai.com",
    [CODE_ENV.STAGING]: "http://cps.union.giant.st.sankuai.com",
    [CODE_ENV.PRODUCTION]: "https://union.sankuai.com",
  },
  [DomainType.MEDIA_V2]: {
    [CODE_ENV.DEVELOP]: "http://mall.pepper.test.sankuai.com",
    [CODE_ENV.TEST]: "http://mall.pepper.test.sankuai.com",
    [CODE_ENV.STAGING]: "http://cps.union.giant.st.sankuai.com",
    [CODE_ENV.PRODUCTION]: "https://union.sankuai.com",
  },
  [DomainType.PEPPER]: {
    [CODE_ENV.DEVELOP]: "http://pepper.mall.test.sankuai.com",
    // [CODE_ENV.TEST]: "http://pepper.mall.test.sankuai.com",
    [CODE_ENV.TEST]: "http://pepper.mall.test.sankuai.com",
    [CODE_ENV.STAGING]: "https://union.sankuai.com",
    [CODE_ENV.PRODUCTION]: "https://union.sankuai.com",
  },
  [DomainType.MAP]: {
    [CODE_ENV.DEVELOP]: "http://lbsapi.map.test.sankuai.com",
    [CODE_ENV.TEST]: "http://lbsapi.map.test.sankuai.com",
    [CODE_ENV.STAGING]: "http://lbsapi.map.st.sankuai.com",
    [CODE_ENV.PRODUCTION]: "https://lbsapi.sankuai.com",
  },
  [DomainType.CLICK]: {
    [CODE_ENV.DEVELOP]: "http://click.tsp.test.sankuai.com", // 开发环境使用相对路径，通过 Vite 代理转发
    [CODE_ENV.TEST]: "https://click.tsp.test.sankuai.com",
    [CODE_ENV.STAGING]: "https://click.st.meituan.com",
    // [CODE_ENV.STAGING]: "http://click.tsp.st.sankuai.com",
    [CODE_ENV.PRODUCTION]: "https://click.sankuai.com",
  },
};

const pepperClient = new HttpClient({
  baseURL:
    // SSR环境使用SSRDomainConfigs，CSR环境使用DomainConfigs
    isServer
      ? SSRDomainConfigs[DomainType.PEPPER][CURRENT_CODE_ENV as CODE_ENV]
      : DomainConfigs[DomainType.PEPPER][CURRENT_CODE_ENV as CODE_ENV],
  timeout: TIMEOUT,
  withCredentials: true,
});

// media域名client
const mediaClient = new HttpClient({
  baseURL:
    // SSR环境使用SSRDomainConfigs，CSR环境使用DomainConfigs
    isServer
      ? SSRDomainConfigs[DomainType.MEDIA][CURRENT_CODE_ENV as CODE_ENV]
      : DomainConfigs[DomainType.MEDIA][CURRENT_CODE_ENV as CODE_ENV],
  timeout: TIMEOUT,
  withCredentials: true,
});

// media域名V2 client（ST环境网关已满，仅改变ST环境域名）
const mediaV2Client = new HttpClient({
  baseURL:
    // SSR环境使用SSRDomainConfigs，CSR环境使用DomainConfigs
    isServer
      ? SSRDomainConfigs[DomainType.MEDIA_V2][CURRENT_CODE_ENV as CODE_ENV]
      : DomainConfigs[DomainType.MEDIA_V2][CURRENT_CODE_ENV as CODE_ENV],
  timeout: TIMEOUT,
  withCredentials: true,
});

const clickClient = new HttpClient({
  baseURL:
    // SSR环境使用SSRDomainConfigs，CSR环境使用DomainConfigs
    isServer
      ? SSRDomainConfigs[DomainType.CLICK][CURRENT_CODE_ENV as CODE_ENV]
      : DomainConfigs[DomainType.CLICK][CURRENT_CODE_ENV as CODE_ENV],
  timeout: TIMEOUT,
  withCredentials: true,
});

export {
  mediaClient,
  mediaV2Client,
  pepperClient,
  clickClient
};
